import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Badge from '@cloudscape-design/components/badge';
import { AIConfig } from '../../api/tickets';
import { prettifyFilterKey, isEmptyFilterValue, formatFilterValue } from '../../utils/filterFormat';

/**
 * Read-only, human-readable summaries of the current configuration.
 * Presentational only — parents pass already-fetched data so these can be
 * reused both on the Settings page ("currently in use") and in the Tickets
 * sync flow (so users can see which config a sync will apply).
 */

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{value}</div>
    </div>
  );
}

export function FiltersSummary({ filters }: { filters: Record<string, unknown> | null | undefined }) {
  const entries = filters
    ? Object.entries(filters).filter(([, v]) => !isEmptyFilterValue(v))
    : [];

  if (!entries.length) {
    return (
      <Box color="text-body-secondary">
        No filters configured — all tickets are fetched from Tickety.
      </Box>
    );
  }

  return (
    <SpaceBetween size="s">
      {entries.map(([key, value]) => (
        <Row key={key} label={prettifyFilterKey(key)} value={formatFilterValue(value)} />
      ))}
    </SpaceBetween>
  );
}

function truncate(text: string, max = 160): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max)}…` : clean;
}

export function AIConfigSummary({ config }: { config: AIConfig | null | undefined }) {
  if (!config) {
    return <Box color="text-body-secondary">AI configuration not loaded.</Box>;
  }

  const userPrompt = config.user_prompt || config.prompt || '';
  const exampleCount = config.examples?.length ?? 0;

  return (
    <SpaceBetween size="s">
      <div>
        <Box variant="awsui-key-label">Model</Box>
        <div>{config.model ? <Badge color="blue">{config.model}</Badge> : '—'}</div>
      </div>
      <div>
        <Box variant="awsui-key-label">System prompt</Box>
        <div>{config.system_prompt ? truncate(config.system_prompt) : <Box color="text-body-secondary" display="inline">Not set</Box>}</div>
      </div>
      <div>
        <Box variant="awsui-key-label">User prompt template</Box>
        <div>{userPrompt ? truncate(userPrompt) : <Box color="text-body-secondary" display="inline">Not set</Box>}</div>
      </div>
      <div>
        <Box variant="awsui-key-label">Few-shot examples</Box>
        <div>{exampleCount}</div>
      </div>
    </SpaceBetween>
  );
}
